import { useMemo, FC } from 'react';

import {
  ITreeNodeData,
  NodeHandler,
  NodeState,
  TreeActions,
  TreeNodeProps,
} from '../interfaces';
import { TreeIcon } from '../styled';
import IndentLines from './IndentLines';
import Switcher from './Switcher';

const TreeNode: FC<TreeNodeProps> = ({
  node,
  depth,
  expandedKeys,
  selectedKeys,
  actions,
  searchQuery,
  renderNode,
  onNodeClick,
}) => {
  const { key, children } = node;

  const nodeState: NodeState = useMemo(
    () => ({
      expanded: expandedKeys.includes(node.key),
      selected: selectedKeys.includes(node.key),
    }),
    [expandedKeys, node.key, selectedKeys],
  );

  const nodeRender =
    renderNode && typeof renderNode === 'function'
      ? renderNode(node, depth, nodeState, actions, searchQuery)
      : defaultRender(node, depth, nodeState, onNodeClick);

  const shouldDisplay =
    !node.options?.filterable ||
    !searchQuery ||
    node.title.toLowerCase().includes(searchQuery.toLowerCase());
  return shouldDisplay ? (
    <>
      <div className="flex items-center space-x-2" key={key}>
        {nodeRender}
      </div>

      {renderChildren(
        children || [],
        depth,
        searchQuery,
        expandedKeys,
        selectedKeys,
        nodeState.expanded,
        actions,
        onNodeClick,
        renderNode,
      )}
    </>
  ) : null;
};

const renderContent = function (
  node: ITreeNodeData,
  onclick?: (node: ITreeNodeData) => void,
) {
  const { title, icon, subtitle } = node;

  let nodeIcon = null;

  if (icon) {
    nodeIcon = <TreeIcon>{icon}</TreeIcon>;
  }

  return (
    <div
      className="flex flex-1 select-none items-center overflow-hidden text-ellipsis whitespace-nowrap"
      draggable={node.options?.draggable}
      onClick={() => {
        if (onclick) onclick(node);
      }}
    >
      {nodeIcon}
      <div
        style={{
          lineHeight: '24px',
          fontSize: 11,
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div className="mx-1 min-w-0 text-xxs italic text-fd-gray-mild">
          {subtitle}
        </div>
      )}
    </div>
  );
};

const renderChildren = function (
  arr: ITreeNodeData[],
  depth: number,
  searchQuery: string,
  expandedKeys: string[],
  selectedKeys: string[],
  expanded: boolean,
  actions: TreeActions,
  onNodeClick?: NodeHandler,
  renderNode?: (...args: any[]) => JSX.Element,
) {
  return arr.length && expanded
    ? arr.map((node) => (
        <TreeNode
          key={node.key}
          actions={actions}
          node={node}
          searchQuery={searchQuery}
          depth={depth + 1}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          onNodeClick={onNodeClick}
          renderNode={renderNode}
        />
      ))
    : null;
};

const defaultRender = function (
  node: ITreeNodeData,
  depth: number,
  state: NodeState,
  onNodeClick?: NodeHandler,
) {
  return (
    <>
      <IndentLines showLines depth={depth} />
      <Switcher
        show={(node.children && node.children.length > 0) || false}
        expanded={state.expanded}
      />
      {renderContent(node, onNodeClick)}
    </>
  );
};

export default TreeNode;
